import { computed, onScopeDispose, shallowReactive, type Ref } from 'vue';
import { getCheckInDates, getDurations, type CheckInDates } from '@/services/api/player';
import { PlayerApiError } from '@/types/errors';
import type { RoomOccupancy } from '@/types/SearchBar';
import { occupancyToPlayerPax } from '@/utils/offers/occupancyToPlayerPax';
import { formatDateLocal } from '@/utils/formatting/dateFormatter';
import { daysBetweenKeys } from '@/utils/calendar/hotelAvailability';

export type HotelArrivalStatus = 'idle' | 'loading' | 'ready' | 'empty' | 'error';
export type HotelDurationsStatus = 'idle' | 'loading' | 'ready' | 'error';

/** Answers older than this are fetched again; the gateway's own cache makes a repeat ~30 ms. */
export const HOTEL_AVAILABILITY_TTL_MS = 10 * 60_000;
/** Own timeout: the Player client allows 30 s with 3 retries, far too long for a calendar. */
export const HOTEL_AVAILABILITY_TIMEOUT_MS = 5_000;
/** `Filter=Duration` over every arrival in the mask window (~500 days from tomorrow). */
const ALL_ARRIVALS_POST_OFFSET_DAYS = 500;

interface Slot<TStatus, TData> {
  status: TStatus;
  data: TData;
  fetchedAt: number;
}

interface Entry {
  arrival: Slot<Exclude<HotelArrivalStatus, 'idle'>, CheckInDates | null>;
  durations: Slot<Exclude<HotelDurationsStatus, 'idle'>, number[]>;
}

type Channel = 'arrival' | 'durations' | 'stay';
type Outcome<T> = { kind: 'ok'; value: T } | { kind: 'failed' } | { kind: 'superseded' };

function tomorrowKey(): string {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  return formatDateLocal(date);
}

function hasArrival(data: CheckInDates | null): boolean {
  return !!data && data.mask.includes('1');
}

/**
 * Per-hotel arrival days and lengths of stay (GetFilters) for the PDP calendar.
 *
 * Nothing is fetched until {@link load} runs (the date modal opening). Answers
 * are cached per product + occupancy for {@link HOTEL_AVAILABILITY_TTL_MS}; a
 * key without a valid answer reports `loading`, never another occupancy's data.
 * A newer request replaces the older one on the same channel and the replaced
 * one is ignored, while a timeout counts as an error.
 */
export function useHotelAvailability(
  productCode: Ref<string>,
  roomOccupancy: Ref<RoomOccupancy>,
  options: { ttlMs?: number; timeoutMs?: number } = {}
) {
  const ttlMs = options.ttlMs ?? HOTEL_AVAILABILITY_TTL_MS;
  const timeoutMs = options.timeoutMs ?? HOTEL_AVAILABILITY_TIMEOUT_MS;

  const entries = shallowReactive(new Map<string, Entry>());
  const stays = new Map<string, { data: number[]; fetchedAt: number }>();
  const controllers: Partial<Record<Channel, AbortController>> = {};
  const inFlightKey: Partial<Record<'arrival' | 'durations', string>> = {};

  const pax = computed(() => occupancyToPlayerPax(roomOccupancy.value));
  const cacheKey = computed(() =>
    JSON.stringify([productCode.value, pax.value.passengers, pax.value.rooms])
  );
  const entry = computed(() => entries.get(cacheKey.value));

  const arrivalStatus = computed<HotelArrivalStatus>(() => entry.value?.arrival.status ?? 'idle');
  const durationsStatus = computed<HotelDurationsStatus>(
    () => entry.value?.durations.status ?? 'idle'
  );
  const checkInDates = computed<CheckInDates | null>(() =>
    entry.value?.arrival.status === 'ready' ? entry.value.arrival.data : null
  );
  const offeredDurations = computed<number[]>(() =>
    entry.value?.durations.status === 'ready' ? entry.value.durations.data : []
  );

  function updateEntry(key: string, patch: Partial<Entry>): void {
    const current = entries.get(key) ?? {
      arrival: { status: 'loading', data: null, fetchedAt: 0 },
      durations: { status: 'loading', data: [], fetchedAt: 0 },
    };
    entries.set(key, { ...current, ...patch });
  }

  function isValid(
    slot: Slot<string, unknown> | undefined,
    channel: 'arrival' | 'durations',
    key: string,
    now: number
  ): boolean {
    if (!slot) return false;
    if (slot.status === 'loading') return inFlightKey[channel] === key;
    return slot.status !== 'error' && now - slot.fetchedAt < ttlMs;
  }

  async function run<T>(
    channel: Channel,
    request: (signal: AbortSignal) => Promise<T>
  ): Promise<Outcome<T>> {
    controllers[channel]?.abort();
    const controller = new AbortController();
    controllers[channel] = controller;

    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);

    try {
      const value = await request(controller.signal);
      if (timedOut) return { kind: 'failed' };
      return controller.signal.aborted ? { kind: 'superseded' } : { kind: 'ok', value };
    } catch (error) {
      if (timedOut) {
        console.warn(`[useHotelAvailability] ${channel} request timed out`);
        return { kind: 'failed' };
      }
      if (controller.signal.aborted) return { kind: 'superseded' };
      console.warn(`[useHotelAvailability] ${channel} request failed`, error);
      return { kind: 'failed' };
    } finally {
      clearTimeout(timer);
      if (controllers[channel] === controller) delete controllers[channel];
    }
  }

  async function loadArrival(key: string, params: Parameters<typeof getCheckInDates>[0]) {
    inFlightKey.arrival = key;
    updateEntry(key, {
      arrival: { status: 'loading', data: null, fetchedAt: 0 },
    });
    const outcome = await run('arrival', (signal) => getCheckInDates(params, { signal }));
    if (outcome.kind === 'superseded') return;
    if (inFlightKey.arrival === key) delete inFlightKey.arrival;

    updateEntry(key, {
      arrival:
        outcome.kind === 'ok'
          ? {
              status: hasArrival(outcome.value) ? 'ready' : 'empty',
              data: outcome.value,
              fetchedAt: Date.now(),
            }
          : { status: 'error', data: null, fetchedAt: Date.now() },
    });
  }

  async function loadDurations(key: string, params: Parameters<typeof getDurations>[0]) {
    inFlightKey.durations = key;
    updateEntry(key, {
      durations: { status: 'loading', data: [], fetchedAt: 0 },
    });
    const outcome = await run('durations', (signal) => getDurations(params, { signal }));
    if (outcome.kind === 'superseded') return;
    if (inFlightKey.durations === key) delete inFlightKey.durations;

    updateEntry(key, {
      durations:
        outcome.kind === 'ok'
          ? { status: 'ready', data: outcome.value, fetchedAt: Date.now() }
          : { status: 'error', data: [], fetchedAt: Date.now() },
    });
  }

  /** Request A (arrival days) and B (all lengths) in parallel, each only when its cache is not valid. */
  async function load(): Promise<void> {
    const key = cacheKey.value;
    const current = entries.get(key);
    const now = Date.now();
    const base = { ...pax.value, productCode: productCode.value };
    const start = tomorrowKey();

    const tasks: Promise<void>[] = [];
    if (!isValid(current?.arrival, 'arrival', key, now)) {
      tasks.push(loadArrival(key, { ...base, start }));
    }
    if (!isValid(current?.durations, 'durations', key, now)) {
      tasks.push(
        loadDurations(key, {
          ...base,
          arrival: start,
          postOffsetDays: ALL_ARRIVALS_POST_OFFSET_DAYS,
        })
      );
    }
    await Promise.all(tasks);
  }

  /**
   * Request C: the lengths sold for one arrival day. Rejects when the request
   * fails, times out or is replaced by a newer call.
   */
  async function durationsFor(arrival: string): Promise<number[]> {
    const stayKey = `${cacheKey.value}|${arrival}`;
    const cached = stays.get(stayKey);
    if (cached && Date.now() - cached.fetchedAt < ttlMs) return cached.data;

    const params = { ...pax.value, productCode: productCode.value, arrival, postOffsetDays: 0 };
    const outcome = await run('stay', (signal) => getDurations(params, { signal }));

    if (outcome.kind === 'ok') {
      stays.set(stayKey, { data: outcome.value, fetchedAt: Date.now() });
      return outcome.value;
    }
    throw new PlayerApiError(
      outcome.kind === 'superseded' ? 'Request was replaced' : 'Durations request failed',
      outcome.kind === 'superseded' ? 'REQUEST_CANCELLED' : 'REQUEST_FAILED',
      'BewotecHubService/GetFilters'
    );
  }

  /** Drops a day from the cached mask once request C showed nothing starts there any more. */
  function markNotArrival(arrival: string): void {
    const key = cacheKey.value;
    const current = entries.get(key);
    const data = current?.arrival.status === 'ready' ? current.arrival.data : null;
    stays.delete(`${key}|${arrival}`);
    if (!data) return;

    const index = daysBetweenKeys(data.from, arrival);
    if (index < 0 || index >= data.mask.length || data.mask[index] !== '1') return;

    const next = {
      from: data.from,
      mask: `${data.mask.slice(0, index)}0${data.mask.slice(index + 1)}`,
    };
    updateEntry(key, {
      arrival: { ...current!.arrival, status: hasArrival(next) ? 'ready' : 'empty', data: next },
    });
  }

  onScopeDispose(() => {
    Object.values(controllers).forEach((controller) => controller?.abort());
  });

  return {
    arrivalStatus,
    durationsStatus,
    checkInDates,
    offeredDurations,
    load,
    durationsFor,
    markNotArrival,
  };
}
